// geo.js — resolve the visitor's country from their REAL public IP, once per
// session, and make every API request carry it (header `X-Geo-Country`) so the
// backend localizes pricing / scoring / rewrite / exports to the right market.
//
// Why the client does this: the server only sees `localhost` in local dev (and
// any prod without a CDN geo header), so it can't place the visitor by itself.
// The browser looks up its own public-IP country via a free, keyless, CORS geo
// service and sends it along. Country only — the visitor can still override with
// the region selector, and it never decides billing (that's the card at
// checkout). Cloudflare's server-side CF-IPCountry still wins over this in prod.
import axios from "axios";

const SS_KEY = "pos_geo_country"; // sessionStorage: ISO-3166-alpha2 for this session
const HEADER = "X-Geo-Country";

function _readStored() {
  try { return sessionStorage.getItem(SS_KEY) || ""; } catch { return ""; }
}
function _store(cc) {
  try { if (cc) sessionStorage.setItem(SS_KEY, cc); } catch { /* storage off */ }
}
function _applyHeader(cc) {
  if (cc) axios.defaults.headers.common[HEADER] = cc;
}

// Public IP -> ISO-2 country. Tries several free/keyless/CORS services in order
// (resilient to one being down or ad-blocked). Uses bare fetch so none of our
// own headers/cookies leak to the third party. Returns "" if all fail — the
// backend then falls back to IN, so an undetected visitor still gets India.
async function _lookup() {
  // Ordered most-reliable / least-ad-blocked first. GeoJS + country.is are rarely
  // on block-lists; ipapi.co is last because uBlock-style lists often block it.
  const sources = [
    { url: "https://get.geojs.io/v1/ip/country.json", pick: (d) => d && d.country },
    { url: "https://api.country.is/", pick: (d) => d && d.country },
    { url: "https://ipwho.is/", pick: (d) => d && d.success !== false && d.country_code },
    { url: "https://ipapi.co/json/", pick: (d) => d && d.country_code },
  ];
  for (const s of sources) {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), 3500);
      const res = await fetch(s.url, { signal: ctrl.signal, credentials: "omit" });
      clearTimeout(timer);
      if (!res.ok) { console.info(`[geo] ${s.url} -> HTTP ${res.status}, trying next`); continue; }
      const cc = String(s.pick(await res.json()) || "").trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(cc)) { console.info(`[geo] detected ${cc} via ${s.url}`); return cc; }
      console.info(`[geo] ${s.url} -> no usable country, trying next`);
    } catch (e) {
      console.info(`[geo] ${s.url} FAILED (${e.name === "AbortError" ? "timeout" : e.message}) — likely blocked; trying next`);
    }
  }
  console.warn("[geo] all IP-geolocation lookups failed — the server will fall back to India. " +
    "In production this is handled server-side by Cloudflare (CF-IPCountry), so this only affects local/blocked cases.");
  return "";
}

let _promise = null;

// Resolve the session's country once (memoized). Sets the axios default header
// as soon as the country is known, so every subsequent API call carries it.
// Dev/QA override (NOT a user-facing picker): `?geo=US` forces the market for
// this session so any market can be verified locally without a VPN. Display only,
// never billing (bound to the payment card at checkout).
function _forcedGeo() {
  try {
    const q = new URLSearchParams(window.location.search).get("geo");
    if (q && /^[A-Za-z]{2}$/.test(q)) return q.toUpperCase();
  } catch { /* no URL */ }
  return "";
}

// SYNCHRONOUS: apply the ?geo= override (or a country already resolved this
// session) to the axios header immediately — NO network. This is what prod uses:
// Cloudflare detects the country server-side (CF-IPCountry), so we never make a
// third-party IP call there. Returns the applied code, or "".
export function applyForcedGeo() {
  const cc = _forcedGeo() || _readStored();
  if (cc) _applyHeader(cc);
  return cc;
}

// ASYNC fallback (local dev / no CDN geo header): resolve the country via a
// third-party IP lookup and set the header. Memoized. Honors ?geo=/cache first.
export function getGeoCountry() {
  if (_promise) return _promise;
  const forced = _forcedGeo();
  if (forced) {
    _store(forced); _applyHeader(forced);
    console.info(`[geo] using ?geo= override: ${forced}`);
    _promise = Promise.resolve(forced);
    return _promise;
  }
  const cached = _readStored();
  if (cached) {
    _applyHeader(cached);
    _promise = Promise.resolve(cached);
    return _promise;
  }
  _promise = _lookup().then((cc) => {
    if (cc) { _store(cc); _applyHeader(cc); }
    return cc;
  });
  return _promise;
}

// Synchronous best-effort read (whatever we've resolved so far this session).
export function currentGeoCountry() { return _readStored(); }
